import dayjs from 'dayjs';

export type DateFilter = {
  dateFrom?: Date | null;
  dateTo?: Date | null;
};

const REVENUE_PERIOD_DATE_FORMAT = 'MMM D, YYYY';
const REVENUE_LEGEND_PERIOD_DATE_FORMAT = 'DD.MM.YY';
const REVENUE_CHART_AXIS_DAY_FORMAT = 'MMM D, YYYY';
const REVENUE_CHART_AXIS_HOUR_FORMAT = 'MMM D, HH:mm';
const REVENUE_CHART_TOOLTIP_DAY_FORMAT = 'MMM D, YYYY';
const REVENUE_CHART_TOOLTIP_HOUR_FORMAT = 'MMM D, YYYY HH:mm';

export type RevenueChartGroupBy = 'day' | 'hour';

export function parseRevenueChartDate(date: string) {
  const parsed = dayjs(date, ['YYYY-MM-DD HH:mm', 'YYYY-MM-DD HH:00', 'YYYY-MM-DD'], true);

  if (parsed.isValid()) {
    return parsed;
  }

  return dayjs(date);
}

const formatRevenueDate = (date: string, format: string): string => {
  const parsed = parseRevenueChartDate(date);

  if (parsed.isValid()) {
    return parsed.format(format);
  }

  const [day, month, year] = date.split('.').map((part) => part.trim());

  if (day && month && year) {
    return dayjs(`${year}-${month}-${day}`).format(format);
  }

  return date;
};

export function formatRevenueChartAxisLabel(date: string, groupBy: RevenueChartGroupBy): string {
  const parsed = parseRevenueChartDate(date);

  if (!parsed.isValid()) {
    return date;
  }

  return parsed.format(groupBy === 'hour' ? REVENUE_CHART_AXIS_HOUR_FORMAT : REVENUE_CHART_AXIS_DAY_FORMAT);
}

export function formatRevenueChartTooltipLabel(date: string, groupBy: RevenueChartGroupBy): string {
  const parsed = parseRevenueChartDate(date);

  if (!parsed.isValid()) {
    return date;
  }

  return parsed.format(groupBy === 'hour' ? REVENUE_CHART_TOOLTIP_HOUR_FORMAT : REVENUE_CHART_TOOLTIP_DAY_FORMAT);
}

export function formatRevenuePeriodDate(date: string): string {
  return formatRevenueDate(date, REVENUE_PERIOD_DATE_FORMAT);
}

export function formatRevenueLegendPeriodDate(date: string): string {
  return formatRevenueDate(date, REVENUE_LEGEND_PERIOD_DATE_FORMAT);
}

export function formatRevenuePeriodRange(start?: string | null, end?: string | null): string | null {
  if (!start || !end) {
    return null;
  }

  return `${formatRevenuePeriodDate(start)} - ${formatRevenuePeriodDate(end)}`;
}

export function formatRevenueComparisonPeriodLabel(label: string): string {
  return label
    .split(' - ')
    .map((part) => formatRevenuePeriodDate(part.trim()))
    .join(' - ');
}

export function getFilterDates(filter?: DateFilter) {
  return {
    dateFrom: dayjs(filter?.dateFrom ?? dayjs().startOf('month')).toISOString(),
    dateTo: dayjs(filter?.dateTo ?? dayjs().endOf('day')).toISOString(),
  };
}

/** Диапазон для Hasura только если указана хотя бы одна граница даты. */
export function buildOptionalTimestampRange(filter?: DateFilter | null): { _gte: string; _lte: string } | undefined {
  if (!filter?.dateFrom && !filter?.dateTo) {
    return undefined;
  }

  return {
    _gte: dayjs(filter.dateFrom ?? dayjs().startOf('month')).toISOString(),
    _lte: dayjs(filter.dateTo ?? dayjs().endOf('day')).toISOString(),
  };
}

/** Для экспорта отчётов: всегда валидный диапазон дат. */
export function getExportFilterDates(filter?: DateFilter) {
  return getFilterDates(filter);
}
